import { DataTable, Skeleton } from "@design";
import { useTranslation } from "react-i18next";
import { Markdown } from "@/components/chat/Markdown";
import { Text } from "@/components/Text";
import type { PreviewFile } from "./previewFile";
import { fenced, parseCsv, type TextKind, useDocumentText } from "./textDocuments";

/** Rows shown in a CSV preview; the full file is a download away. */
const MAX_CSV_ROWS = 500;

interface CsvRow {
  index: number;
  cells: string[];
}

// conventions-ignore: no-raw-colors a web page's own content assumes a white page, in light and dark mode.
const HTML_FRAME_CLASS = "h-[70dvh] w-full rounded-md border border-border-subtle bg-white";

function CsvTable({ text, title }: Readonly<{ text: string; title: string }>) {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const [header = [], ...body] = parseCsv(text);
  const rows: CsvRow[] = body.slice(0, MAX_CSV_ROWS).map((cells, index) => ({ index, cells }));
  return (
    <div className="space-y-2">
      <DataTable<CsvRow>
        aria-label={title}
        rowKey={(row) => String(row.index)}
        rows={rows}
        columns={header.map((name, i) => ({
          key: String(i),
          header: name,
          render: (row) => row.cells[i] ?? "",
        }))}
      />
      {body.length > MAX_CSV_ROWS && (
        <Text variant="caption" tone="tertiary">
          {t("rowsTruncated", { shown: MAX_CSV_ROWS, total: body.length })}
        </Text>
      )}
    </div>
  );
}

function Rendered({ kind, text, title }: Readonly<{ kind: TextKind; text: string; title: string }>) {
  if (kind === "markdown") return <Markdown>{text}</Markdown>;
  if (kind === "csv") return <CsvTable text={text} title={title} />;
  // Generated HTML is untrusted: an empty sandbox runs no scripts and gets an opaque origin.
  return <iframe title={title} sandbox="" srcDoc={text} className={HTML_FRAME_CLASS} />;
}

/** A Markdown, CSV or HTML file, rendered (`view="preview"`) or as its text (`view="source"`). */
export function TextDocumentPreview({
  file,
  kind,
  title,
  view,
}: Readonly<{ file: PreviewFile; kind: TextKind; title: string; view: "preview" | "source" }>) {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const text = useDocumentText(file);
  if (text.isPending) return <Skeleton className="h-64 w-full rounded-md" />;
  if (text.isError) {
    return (
      <output className="block rounded-lg border border-border-subtle bg-surface-secondary px-4 py-6 text-center">
        <Text tone="secondary">{t("previewError")}</Text>
      </output>
    );
  }
  if (view === "source") return <Markdown>{fenced(text.data, kind)}</Markdown>;
  return <Rendered kind={kind} text={text.data} title={title} />;
}
